<template>
    <EditWindow v-if="data.show" heading="Offer group to teachers" :name="group_label" :width="560" @close="close_window()">

        <p class="og-help">Tick the teachers who should get a WhatsApp message offering them this group.</p>

        <div class="og-search">
            <span class="material-symbols-outlined" aria-hidden="true">search</span>
            <input v-model="query" type="search" placeholder="Search teachers…" autocomplete="off" aria-label="Search teachers">
        </div>

        <div class="og-count">
            {{ data.selected.length }} selected
            <button v-if="data.selected.length" type="button" class="og-link" @click.stop="data.selected=[]">Clear</button>
        </div>

        <ul class="og-list">
            <li v-for="instructor in visible_instructors" :key="instructor.id">
                <label class="og-item" :class="{selected:data.selected.includes(instructor)}">
                    <input type="checkbox" :checked="data.selected.includes(instructor)" @change="toggle_selected(instructor)">
                    <span class="og-name">{{ instructor.user.first_name }} {{ instructor.user.last_name }}</span>
                    <span v-if="!instructor.user.cellphone" class="og-nophone" title="No phone number on file">no phone</span>
                </label>
            </li>
        </ul>
        <p v-if="!data.instructors.length" class="og-empty">Loading teachers…</p>
        <p v-else-if="!visible_instructors.length" class="og-empty">No teachers match “{{ query }}”.</p>

        <template #footer="{close}">
            <button type="button" class="og-btn" @click.stop="close()">Close</button>
            <button type="button" class="og-btn og-btn-primary" :disabled="!data.selected.length || sending"
                    @click.stop="send_group_offers()">
                <span class="material-symbols-outlined" aria-hidden="true">send</span>
                {{ sending ? 'Sending…' : `Send offer${data.selected.length==1 ? '' : 's'}` }}
            </button>
        </template>
    </EditWindow>
</template>



<script setup>

    import {reactive,inject,ref,computed,watch} from 'vue'
    import axios from 'axios'
    import EditWindow from '@/components/edit/EditWindow.vue'
    import {ask_whatsapp} from '@/confirm'

    let data = reactive({
        show:false,
        hide:true,
        selected:[],
        instructors:[],
        group:null,
    })
    data=inject('offer_data')

    let query=ref('')
    let sending=ref(false)

    const group_label=computed(()=>{
        const g=data.group
        if(!g){ return '' }
        return [g.subject, g.schedule].filter(Boolean).join(' · ') || g.ui
    })

    function fold(text){
        return String(text||'').normalize('NFD').replace(/[̀-ͯ]/g,'').toLowerCase()
    }
    const visible_instructors=computed(()=>{
        const q=fold(query.value.trim())
        return data.instructors.filter(i=>!q || fold(`${i.user.first_name} ${i.user.last_name}`).includes(q))
    })

    watch(()=>data.show,open=>{ if(open){ query.value='' } })

    function close_window(){
        data.show=false
        data.hide=true
        data.selected=[]

    }

    function toggle_selected(teacher){
        if(data.selected.includes(teacher)){
            data.selected = data.selected.filter(t=>t!=teacher)
        } else {
        data.selected.push(teacher)
    }
}

    async function send_group_offers(){
        const n=data.selected.length
        const go=await ask_whatsapp({
            title:`Send the group offer to ${n} teacher${n==1 ? '' : 's'}?`,
            to:data.selected.map(i=>({ui:`${i.user.first_name} ${i.user.last_name}`.trim(),user:i.user})),
            message:`Each one gets a WhatsApp message offering them “${group_label.value}”.`,
            confirm_label:`Send ${n} message${n==1 ? '' : 's'}`,
        })
        if(!go){
            return
        }

        let instructor_ids=[]

        data.selected.forEach(instructor => {
            instructor_ids.push(instructor.id)
        })

        sending.value=true
        try{
            let response=await axios.post('/admin/send_group_offer/',{group_id:data.group.id,instructors_ids:instructor_ids})
            const names=data.selected.map(i=>i.user.first_name).join(', ')
            alert(`Group offer sent to: ${names}`)
            data.selected=[]
        }finally{
            sending.value=false
        }

    }


</script>



<style scoped>

    .og-help{
        margin:0;
        color:var(--ew-muted);
    }

    .og-search{
        position:sticky;
        top:0;
        z-index:1;
        display:flex;
        align-items:center;
        gap:8px;
        padding:0 12px;
        background:#ffffff;
        border:1px solid var(--ew-field-line);
        border-radius:10px;
    }
    .og-search:focus-within{
        outline:2px solid var(--ew-focus);
        outline-offset:-1px;
    }
    .og-search .material-symbols-outlined{ color:var(--ew-muted); }
    .og-search input{
        flex:1;
        min-width:0;
        height:44px;
        font-size:16px;
        color:var(--ew-text);
        background:transparent;
        border:none;
        outline:none;
    }

    .og-count{
        display:flex;
        align-items:center;
        gap:10px;
        font-size:13px;
        color:var(--ew-muted);
    }
    .og-link{
        width:auto;
        padding:2px 4px;
        font-size:13px;
        color:var(--ew-focus);
        background:transparent;
        border:none;
        box-shadow:none;
        text-decoration:underline;
    }
    .og-link:hover{
        color:#055a7c;
        background:transparent;
        box-shadow:none;
    }

    .og-list{
        display:flex;
        flex-direction:column;
        margin:0;
        padding:0;
        list-style:none;
        background:var(--ew-card);
        border:1px solid var(--ew-line);
        border-radius:10px;
    }
    .og-list li + li{ border-top:1px solid var(--ew-line); }
    .og-item{
        display:flex;
        align-items:center;
        gap:12px;
        min-height:48px;
        padding:6px 14px;
        cursor:pointer;
    }
    .og-item:hover{ background:rgba(6,115,158,0.06); }
    .og-item.selected{ background:rgba(42,138,110,0.10); }
    .og-item input{
        flex:none;
        width:20px;
        height:20px;
        accent-color:var(--ew-ok);
    }
    .og-name{ flex:1; overflow-wrap:anywhere; }
    .og-nophone{
        padding:1px 7px;
        font-size:11px;
        color:#7a4f00;
        background:#fff1cc;
        border-radius:999px;
    }

    .og-empty{
        margin:0;
        color:var(--ew-muted);
        font-style:italic;
    }

    .og-btn{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        gap:6px;
        min-width:96px;
        height:42px;
        min-height:42px;
        padding:0 18px;
        font-size:15px;
        font-weight:600;
        word-break:normal;
        color:var(--ew-text);
        background:#ffffff;
        border:1px solid var(--ew-field-line);
        border-radius:10px;
        box-shadow:none;
    }
    .og-btn:hover{
        color:var(--ew-text);
        background:var(--ew-bg);
        box-shadow:none;
    }
    .og-btn-primary{
        color:#ffffff;
        background:var(--ew-ok);
        border-color:var(--ew-ok);
    }
    .og-btn-primary:hover{
        color:#ffffff;
        background:#22735b;
    }
    .og-btn:disabled{
        opacity:0.5;
        cursor:not-allowed;
    }
    .og-btn .material-symbols-outlined{ font-size:20px; }

    @media (max-width:600px){
        .og-btn{ flex:1 1 0; }
    }

</style>
